// SeasonLoadChart — minimal area chart of weekly load across the season.
// Teal gradient fill, current week marked, no axis clutter.

import {
  AreaChart,
  Area,
  XAxis,
  ReferenceLine,
  ResponsiveContainer,
} from 'recharts';
import type { SeasonSummaryPhase } from '@/lib/api';

const TEAL = '0, 255, 200';

interface LoadPoint {
  week: string;
  load: number;
  status: 'completed' | 'current' | 'upcoming';
}

interface SeasonLoadChartProps {
  phases: SeasonSummaryPhase[];
  className?: string;
}

export function SeasonLoadChart({ phases, className }: SeasonLoadChartProps) {
  const data: LoadPoint[] = phases.flatMap(phase =>
    phase.weeks.map(week => ({
      week: `W${week.week_index}`,
      load: week.key_sessions.length * 20,
      status: week.status,
    }))
  );

  if (data.length === 0) return null;

  const currentWeek = data.find(d => d.status === 'current');

  return (
    <div className={className} style={{ height: 56 }}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 4, right: 2, bottom: 0, left: 2 }}>
          <defs>
            <linearGradient id="seasonLoadGradient" x1="0" y1="0" x2="0" y2="1">
              <stop
                offset="0%"
                stopColor={`rgba(${TEAL}, 0.35)`}
                stopOpacity={1}
              />
              <stop
                offset="100%"
                stopColor={`rgba(${TEAL}, 0.02)`}
                stopOpacity={1}
              />
            </linearGradient>
          </defs>
          <XAxis dataKey="week" hide />
          {currentWeek && (
            <ReferenceLine
              x={currentWeek.week}
              stroke={`rgba(${TEAL}, 0.45)`}
              strokeDasharray="3 3"
              strokeWidth={1}
            />
          )}
          <Area
            type="monotone"
            dataKey="load"
            stroke={`rgba(${TEAL}, 0.55)`}
            strokeWidth={1.5}
            fill="url(#seasonLoadGradient)"
            dot={false}
            activeDot={false}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
